import {
  useGetEnrollmentMultiInvestmentGoalsData,
  useSubmitEnrollmentMultiInvestmentGoalsData,
  EnrollmentInvestmentExperienceCategory,
  EnrollmentInvestmentObjectiveCategory,
} from '@egw/api'
import { Button, GenericError, ProgressBar, RadioGroup } from '@egw/components'
import { useTranslation } from '@egw/i18n'
import { EnrollmentMultiLayout } from '@egw/layouts'
import { Logger } from '@egw/logger'
import { useEffect, useState, type FC } from 'react'
import { Controller, useForm } from 'react-hook-form'
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton'
import { useNavigate } from 'react-router'
import { PageHeader } from '../../../components'
import { ROUTES } from '../../../constants/routes/routes-generic'
import { useValidateRoute } from '../../../hooks/navigation/validate-route'
import { sortInvestmentOptionGoals } from '../../../utils/investments/sort-investment-option-goals'
import { sortInvestmentOptionExperiences } from '../../../utils/investments/sort-investment-option-experiences'

const log = new Logger('Enrollment - Multi - InvestmentGoals')

interface FormData {
  objective: string
  experience: string
}

export const InvestmentGoalsPage: FC = () => {
  const { t } = useTranslation('features-enrollment')
  const navigate = useNavigate()

  const { data, isLoading, error } = useGetEnrollmentMultiInvestmentGoalsData()
  const { mutate, isLoading: isSubmitting, error: submitError } = useSubmitEnrollmentMultiInvestmentGoalsData()
  const progress = data?.__typename === 'SubmitEnrollmentSuccess' ? data.enrollmentData.progress : undefined
  const [hasFormInitialized, setHasFormInitialized] = useState(false)

  // Protect route
  useValidateRoute(data?.path)

  // AUTO GENERATED FORM HANDLERS START
  const {
    control,
    handleSubmit,
    formState: { errors: formErrors, isValid },
    reset,
  } = useForm({
    defaultValues: {
      objective: '',
      experience: '',
    },
    mode: 'onChange',
  })

  useEffect(() => {
    if (data?.__typename === 'SubmitEnrollmentSuccess' && data.enrollmentData && !hasFormInitialized) {
      reset({
        objective: data.enrollmentData.investmentProfile.objective,
        experience: data.enrollmentData.investmentProfile.experience,
      })
      setHasFormInitialized(true)
    }
  }, [data, reset, hasFormInitialized])

  const onSubmit = handleSubmit(async (fData: unknown): Promise<void> => {
    const formData = fData as FormData

    try {
      const response = await mutate({
        investmentProfile: {
          objective: formData.objective as any,
          experience: formData.experience as any,
        },
      })
      if (response?.path) {
        void navigate(response.path)
      } else if (!response?.path) {
        void navigate(ROUTES.ERROR.path)
      }
    } catch (e) {
      log.error('Error submitting email', e)
    }
  })
  // AUTO GENERATED FORM HANDLERS END

  return (
    <EnrollmentMultiLayout title={t('multi.investment-goals.page_title')}>
      {/* Loading initial data to make sure the user has access to this screen and retreive data based on user enrollment application */}
      {isLoading ? (
        <div className="w-full">
          <SkeletonTheme baseColor="#eeefee" highlightColor="rgba(255, 255, 255, 0.5)">
            <Skeleton count={1} height="100px" className="mb-10" />
            <Skeleton count={5} height="50px" />
          </SkeletonTheme>
        </div>
      ) : null}

      {/* In some cases initial data can be null, so we assume if it's not loading and there's no error - we show the page */}
      {!isLoading && !error ? (
        <div className="w-full flex-1 flex flex-col justify-start">
          {/* AUTO GENERATED COMPONENTS START*/}
          <PageHeader className="mb-sm" title={t('multi.investment-goals.title')} />
          <ProgressBar className="mb-md" progressPercentage={progress} showAnimation />
          <p className="typo-primary-base mb-xl text-text-meta">{t('multi.investment-goals.description')}</p>
          <form onSubmit={(e) => void onSubmit(e)} className="flex flex-col h-full">
            <p className="typo-primary-base-semibold mb-sm">{t('multi.investment-goals.objective_title')}</p>
            <Controller
              name="objective"
              control={control}
              rules={{
                required: true,
              }}
              render={({ field: { ...restField } }) => (
                <RadioGroup
                  {...restField}
                  options={sortInvestmentOptionGoals(
                    Object.values(EnrollmentInvestmentObjectiveCategory).map((item) => ({
                      value: item,
                      label: t(`multi.investment-goals.investment_objective.${item}`),
                    })),
                  )}
                  name="objective"
                  className="!py-xs mb-xl"
                  testId="objective-radio"
                />
              )}
            />

            <p className="typo-primary-base-semibold mb-sm">{t('multi.investment-goals.experience_title')}</p>
            <Controller
              name="experience"
              control={control}
              rules={{
                required: true,
              }}
              render={({ field: { ...restField } }) => (
                <RadioGroup
                  {...restField}
                  options={sortInvestmentOptionExperiences(
                    Object.values(EnrollmentInvestmentExperienceCategory).map((item) => ({
                      value: item,
                      label: t(`multi.investment-goals.investment_experience.${item}`),
                    })),
                  )}
                  name="experience"
                  className="!py-xs mb-xl"
                  testId="experience-radio"
                />
              )}
            />

            <div className="mt-auto md:mt-0">
              <Button
                type="submit"
                className="w-full md:w-min"
                isDisabled={!isValid}
                isLoading={isSubmitting}
                testId="enrollment-continue-button"
              >
                {t('common.continue')}
              </Button>
            </div>
          </form>
          {/* AUTO GENERATED COMPONENTS END*/}
        </div>
      ) : null}

      {/* This error handles the case when the initial data is not loaded, for example network error */}
      {error ? (
        <div className="flex-1 flex flex-col justify-start md:justify-center">
          <GenericError errorMessage={t('common.generic_error')} />
        </div>
      ) : null}
    </EnrollmentMultiLayout>
  )
}
